import { PurchaseTypeEnum } from 'api-hooks/order/common/model';
import { PurchaseRelatedModel } from 'api-hooks/sale/common';
import { TicketSaleModel } from 'api-hooks/sale/ticket-sale';
import { TourModel } from 'api-hooks/tour/tour-list/tour-detail';
import { themedStyled } from 'common/styles/styletron.theme';
import { distinct } from 'common/utils/iterable';
import { generateId } from 'common/utils/string';
import Separator from 'components/common/separator';
import { Input } from 'components/elements';
import { useFormState } from 'components/elements/form';
import Text from 'components/elements/text';
import AgentSelectInput from 'legacy-modules/master/agent/components/select-input';
import PaymentChannelSelectInput from 'legacy-modules/master/payment-channel/components/select-input';
import AgentSupendToast from 'legacy-modules/order/component/agent-suspend-toast';
import PurchaseRelatedTable from 'legacy-modules/sales/component/purchase-related-table';
import TransactionAt from 'legacy-modules/sales/component/transaction-at';
import TicketSaleInvoiceSelectInput from 'legacy-modules/sales/ticket-sale/components/invoice-select-input';
import { guestNameTransformer } from 'legacy-modules/sales/ticket-sale/hooks/use-get-default-values';
import {
  CardSection,
  HalfContainer,
  SectionContainer as RawSectionContainer,
  SeparatorVertical,
} from 'legacy-modules/styles';
import TourListSelectInput from 'legacy-modules/tour/tour-list/components/select-input';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import {
  TicketOrderFormType,
  TicketOrderFlightPurchaseFormType,
  TicketOrderFlightPurchaseAirlineFormType,
} from './form-type';
import { generateUniqueAirlineItem } from '../hooks/use-get-default-values';

interface Props {}

export default function PurchasesDetailCard(props: Props) {
  const { t } = useTranslation();
  const [purchases, setPurchases] = React.useState<PurchaseRelatedModel[]>([]);

  const { control, setValue, getValues, reset } =
    useFormContext<TicketOrderFormType>();

  const [type, flightSale, flightPurchase, total, remainingTransactionQuota] =
    useWatch({
      control,
      name: [
        'type',
        'flightSale',
        'flightPurchase',
        'total',
        'remainingTransactionQuota',
      ],
    });

  const isExceedingQuota =
    remainingTransactionQuota && total > remainingTransactionQuota;

  React.useEffect(() => {
    setValue('isExceedingQuota', isExceedingQuota || false);
  }, [setValue, isExceedingQuota]);

  const invoiceId = getValues('invoiceId');
  React.useEffect(() => {
    if (!invoiceId || invoiceId === '') {
      reset();
      setPurchases([]);
    }
  }, [invoiceId, reset]);

  const { editable } = useFormState();

  const tourInvoiceOnAfterChange = React.useCallback(
    (value: TourModel) => {
      if (editable) {
        const flightPurchases: TicketOrderFlightPurchaseFormType[] =
          value?.tourFlights?.map((tf) => {
            const flightPurchaseAddOns: TicketOrderFlightPurchaseFormType['flightPurchaseAddOns'] =
              [];
            const flightPurchaseAirlines: TicketOrderFlightPurchaseFormType['flightPurchaseAirlines'] =
              tf.tourFlightAirlines.map((tfa) => {
                const airline = tfa.airline;
                const preference = tfa.preference;
                return {
                  airlineId: airline.id,
                  airlineName: airline.name,
                  customId: generateId(),
                  date: tfa.date || undefined,
                  detail: tfa.detail || '',
                  preferenceId: preference?.id || '',
                  preferenceName: preference?.name || '',
                  price: tfa.price || 0,
                  route: tfa.route || '',
                  time: tfa.time || '',
                } as never as TicketOrderFlightPurchaseAirlineFormType;
              });
            const flightPurchaseGuests: TicketOrderFlightPurchaseFormType['flightPurchaseGuests'] =
              [];

            return {
              bookingCode: '',
              customId: generateId(),
              flightPurchaseAddOns,
              flightPurchaseAirlines,
              flightPurchaseGuests,
            };
          }) ?? [];

        if (flightPurchases.length) {
          setValue('flightPurchases', flightPurchases, {
            shouldTouch: true,
            shouldValidate: true,
          });
        }
      }
    },
    [editable, setValue],
  );

  const invoiceOnAfterChange = React.useCallback(
    (value: TicketSaleModel) => {
      if (editable) {
        if (!flightPurchase) {
          setPurchases(value.purchases);
        }

        const flightPurchases: TicketOrderFlightPurchaseFormType[] =
          value.flightSales.map((fs) => {
            const flightPurchaseAddOns: TicketOrderFlightPurchaseFormType['flightPurchaseAddOns'] =
              fs.flightSaleAddOns.map((fsao) => {
                const guest = fsao.guest;
                return {
                  customId: generateId(),
                  description: fsao.description || '',
                  guestId: guest.id,
                  guestName: guestNameTransformer(guest),
                  id: fsao.id,
                  price: fsao.price || 0,
                  route: fsao.route || '',
                };
              });

            const flightPurchaseAirlines: TicketOrderFlightPurchaseFormType['flightPurchaseAirlines'] =
              distinct(fs.flightSaleGuests, (guest) =>
                generateUniqueAirlineItem(guest),
              ).map((fsg) => {
                const airline = fsg.airline;
                const preference = fsg.preference;
                return {
                  airlineId: airline.id,
                  airlineName: airline.name,
                  customId: generateId(),
                  date: fsg.date,
                  detail: fsg.detail || '',
                  preferenceId: preference?.id || '',
                  preferenceName: preference?.name || '',
                  price: fsg.price || 0,
                  route: fsg.route || '',
                  time: fsg.time || '',
                };
              });

            const flightPurchaseGuests: TicketOrderFlightPurchaseFormType['flightPurchaseGuests'] =
              distinct(fs.flightSaleGuests, (fsg) => fsg.guest.id).map(
                (fsg) => {
                  const guest = fsg.guest;
                  return {
                    customId: generateId(),
                    guestId: guest.id,
                    guestName: guestNameTransformer(guest),
                    ticket: fsg.ticket || '',
                  };
                },
              );

            return {
              bookingCode: fs.bookingCode || '',
              customId: generateId(),
              flightPurchaseAddOns,
              flightPurchaseAirlines,
              flightPurchaseGuests,
            };
          });

        setValue('flightPurchases', flightPurchases, {
          shouldTouch: true,
          shouldValidate: true,
        });
        setValue('discount', value.discount, {
          shouldTouch: true,
          shouldValidate: true,
        });
        setValue('otherCost', value.otherCost, {
          shouldTouch: true,
          shouldValidate: true,
        });
        setValue('invoiceId', value.id);
        setValue('saleTotal', value.total);
        setValue('invoiceRefId', value.refId, {
          shouldTouch: true,
          shouldValidate: true,
        });
      }
    },
    [editable, flightPurchase, setValue],
  );

  const onEnumAfterChange = () => {
    setPurchases([]);
    setValue('invoiceId', undefined, {
      shouldTouch: true,
      shouldValidate: true,
    });
    setValue('tourId', undefined, {
      shouldTouch: true,
      shouldValidate: true,
    });
    setValue('invoiceRefId', undefined, {
      shouldTouch: true,
      shouldValidate: true,
    });
    setValue('tourRefId', undefined, {
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  const agentId = getValues('agentId');
  React.useEffect(() => {
    if (!agentId || agentId === '') {
      setValue('agentStatus', '');
      setValue('remainingTransactionQuota', null);
      setValue('isExceedingQuota', false);
    }
  }, [agentId, setValue]);

  const onAfterAgentDetailChange = React.useCallback(
    (result) => {
      if (editable) {
        const remainingTransactionQuota =
          result.system.remainingTransactionQuota;
        const isExceedingQuota =
          result.system.isTransactionLimited &&
          total > remainingTransactionQuota;

        setValue('isExceedingQuota', isExceedingQuota);
        setValue(
          'remainingTransactionQuota',
          remainingTransactionQuota || null,
        );
        setValue('agentStatus', result.status);
      }
    },
    [editable, setValue, total],
  );

  const disabled = !!flightPurchase || !!flightSale;

  return (
    <>
      <CardSection style={{ paddingBottom: 0 }}>
        <Text variant="HeadingXSmall">{t('ticketOrders:orders-details')}</Text>
        <SeparatorVertical $margin={24} />
        <SectionContainer>
          {flightPurchase && (
            <HalfContainer>
              <Input
                type="text"
                name="orderNumber"
                label={t('common:order_number')}
                placeholder={t('common:order_number')}
                disabled
              />
            </HalfContainer>
          )}
          <HalfContainer
            $style={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
            }}
          >
            <TransactionAt
              name="transactionAt"
              clearable={false}
              hasTime
              required
            />
          </HalfContainer>
        </SectionContainer>
        <SectionContainer>
          <HalfContainer>
            <Input
              type="select"
              label={t('common:type')}
              placeholder={t('common:type')}
              name="type"
              required
              onAfterChange={onEnumAfterChange}
              clearable={false}
              disabled={disabled}
              options={[
                { id: PurchaseTypeEnum.Sales, label: t('common:sales') },
                { id: PurchaseTypeEnum.Tour, label: t('common:tour') },
              ]}
            />
          </HalfContainer>
          <HalfContainer>
            {type === PurchaseTypeEnum.Sales ? (
              <TicketSaleInvoiceSelectInput
                label={t('common:invoice_number')}
                placeholder={t('common:choose_invoice')}
                name="invoiceId"
                onAfterDetailChange={invoiceOnAfterChange}
                disabled={disabled}
                required
              />
            ) : (
              type === PurchaseTypeEnum.Tour && (
                <TourListSelectInput
                  name="tourId"
                  required
                  disabled={!!flightPurchase}
                  onAfterDetailChange={tourInvoiceOnAfterChange}
                />
              )
            )}
          </HalfContainer>
        </SectionContainer>
        <SectionContainer>
          <HalfContainer>
            <AgentSelectInput
              name="agentId"
              remainingTransactionQuota={remainingTransactionQuota}
              onAfterDetailChange={onAfterAgentDetailChange}
              required
            />
          </HalfContainer>
          <HalfContainer>
            <PaymentChannelSelectInput name="paymentChannelId" />
          </HalfContainer>
        </SectionContainer>
        <AgentSupendToast />
      </CardSection>
      {type === PurchaseTypeEnum.Sales &&
        !flightPurchase &&
        !!purchases.length && (
          <>
            <Separator gap={24} />
            <Text variant="HeadingXSmall">{t('common:related_purchases')}</Text>
            <Separator gap={8} />
            <PurchaseRelatedTable
              data={purchases}
              navigation="FlightPurchasesView"
            />
          </>
        )}

      <Separator gap={24} />
    </>
  );
}

const SectionContainer = themedStyled(RawSectionContainer, {
  width: '100%',
});
